import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { matchQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from 'react-use';
import { useAuthContext } from '@lp/core/auth';
import { atTxCenterStatuses } from '@features/components/CaseCard/constants';
import { caseQueryKeys, getCachedCaseListItems } from '@/queries/case';
import { useDeviceTrackingListQuery } from '@/queries/device-tracking';
import { CaseDetailsPermissions } from '@/permissions';

const CASE_IDS_DEBOUNCE_MS = 500;

const useAtTxCenterCaseIds = () => {
  const queryClient = useQueryClient();

  const getSnapshot = useCallback(
    () =>
      getCachedCaseListItems()
        .filter((c) => atTxCenterStatuses.includes(c.status))
        .map((c) => c.caseId)
        .sort((a, b) => a.localeCompare(b))
        .join(','),
    [],
  );

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      queryClient.getQueryCache().subscribe((event) => {
        if (matchQuery({ queryKey: caseQueryKeys.getInfiniteList(), exact: false }, event.query)) {
          onStoreChange();
        }
      }),
    [queryClient],
  );

  const idsJoined = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const [debouncedIdsJoined, setDebouncedIdsJoined] = useState(idsJoined);
  useDebounce(() => setDebouncedIdsJoined(idsJoined), CASE_IDS_DEBOUNCE_MS, [idsJoined]);

  return useMemo(() => (debouncedIdsJoined ? debouncedIdsJoined.split(',') : []), [debouncedIdsJoined]);
};

export const useCasesBoardDeviceTracking = () => {
  const { checkPermission } = useAuthContext();
  const hasReadPermission = checkPermission(CaseDetailsPermissions.LocationTrackingRead);
  const caseIds = useAtTxCenterCaseIds();
  const enabled = hasReadPermission && caseIds.length > 0;

  useDeviceTrackingListQuery({ caseIds, itemsPerPage: 1000 }, enabled);
};
